/**
 * Conditional question logic (show / hide branching).
 *
 * A radio question can route to follow-up questions on a per-answer basis —
 * conceptually "if the customer picks answer A, reveal question X". This is the
 * same per-option pattern the form builder already uses for "request-only" and
 * per-option user auto-assignment, so it lives in the same `optionsJson` blob
 * and needs no backend changes.
 *
 * Wire format (on the *controlling* radio question's `optionsJson`):
 *
 *   {
 *     "options": ["cabinets", "exterior", "interior"],
 *     "optionTargets": { "0": "<questionId-3>", "1": "<questionId-2>", "2": "<questionId-4>" }
 *   }
 *
 * Meaning: each key is the option index, each value is the id of the question
 * that option reveals. A question that is the target of *any* option is treated
 * as conditional: it stays hidden until one of the answers that targets it is
 * selected. Questions that aren't targeted by anything are always visible.
 */

export interface ConditionalQuestionLike {
  id?: string | null;
  /** Field type (e.g. 'Checkbox', 'Dropdown', 'Radio', 'ShortText'...). */
  type?: string | null;
  optionsJson?: string | null;
}

/** Parse the options array from a question's `optionsJson` (legacy array or object). */
export function parseQuestionOptions(optionsJson: string | null | undefined): string[] {
  if (!optionsJson) return [];
  try {
    const parsed = JSON.parse(optionsJson);
    if (Array.isArray(parsed)) return parsed as string[];
    if (parsed && Array.isArray(parsed.options)) return parsed.options as string[];
    return [];
  } catch {
    return [];
  }
}

/**
 * Parse the per-option reveal-target map: option index → target branchKey.
 * Returns an empty object when none are configured.
 */
export function parseOptionTargets(
  optionsJson: string | null | undefined
): Record<number, string> {
  if (!optionsJson) return {};
  try {
    const parsed = JSON.parse(optionsJson);
    if (!parsed || Array.isArray(parsed) || typeof parsed !== 'object') return {};
    const raw = parsed.optionTargets;
    if (!raw || typeof raw !== 'object') return {};
    const result: Record<number, string> = {};
    Object.entries(raw as Record<string, unknown>).forEach(([k, v]) => {
      const idx = Number(k);
      if (Number.isFinite(idx) && idx >= 0 && typeof v === 'string' && v.length > 0) {
        result[idx] = v;
      }
    });
    return result;
  } catch {
    return {};
  }
}

/** Parse this question's stable branch-target identifier, if any. */
export function parseBranchKey(optionsJson: string | null | undefined): string | undefined {
  if (!optionsJson) return undefined;
  try {
    const parsed = JSON.parse(optionsJson);
    if (parsed && !Array.isArray(parsed) && typeof parsed === 'object' && typeof parsed.branchKey === 'string') {
      return parsed.branchKey;
    }
  } catch {
    /* ignore */
  }
  return undefined;
}

/**
 * Collect the set of branchKeys that are revealed conditionally (i.e. that
 * appear as the target of at least one option on some question).
 */
export function getConditionalTargetKeys(
  questions: ConditionalQuestionLike[]
): Set<string> {
  const keys = new Set<string>();
  for (const q of questions) {
    const targets = parseOptionTargets(q.optionsJson);
    for (const targetKey of Object.values(targets)) {
      if (targetKey) keys.add(targetKey);
    }
  }
  return keys;
}

/**
 * Determine whether a question should currently be shown, given the answers
 * collected so far.
 *
 * @param question  The question whose visibility we're evaluating.
 * @param questions All questions on the form (used to find controllers).
 * @param answers   Map of questionId → answer value. For radio/single-select
 *                  the value is the chosen option string.
 */
export function isQuestionVisible(
  question: ConditionalQuestionLike,
  questions: ConditionalQuestionLike[],
  answers: Record<string, string | undefined>,
  // Internal: ids on the current reveal-chain being evaluated, used to guard
  // against cycles in (mis)configured conditional graphs.
  visiting: Set<string> = new Set()
): boolean {
  const myBranchKey = parseBranchKey(question.optionsJson);
  if (!myBranchKey) return true;

  const conditionalKeys = getConditionalTargetKeys(questions);
  // Not a conditional target → always visible.
  if (!conditionalKeys.has(myBranchKey)) return true;

  // Cycle guard: if we're already evaluating this question further up the
  // reveal-chain, treat it as not (yet) revealed to avoid infinite recursion.
  if (question.id) {
    if (visiting.has(question.id)) return false;
    visiting.add(question.id);
  }

  // Conditional → visible only if a controlling question's selected answer
  // targets this question's branchKey *and that controller is itself visible*.
  // Without the controller-visibility check, a stale answer left behind on a
  // now-hidden controller would keep revealing this follow-up — causing
  // conditional questions to reappear unexpectedly when an upstream answer
  // changes (e.g. chained "property type → commercial space → details").
  for (const controller of questions) {
    if (!controller.id) continue;
    if (controller.id === question.id) continue;
    const targets = parseOptionTargets(controller.optionsJson);
    if (Object.keys(targets).length === 0) continue;

    const answer = answers[controller.id];
    if (answer == null || answer === '') continue;

    // A checkbox/multi-select answer is the selected options joined by '||';
    // any selected option that targets this branchKey reveals it.
    const options = parseQuestionOptions(controller.optionsJson);
    const selectedValues = answer.split('||');
    const controllerTargetsMe = selectedValues.some((value) => {
      const selectedIndex = options.indexOf(value);
      return selectedIndex >= 0 && targets[selectedIndex] === myBranchKey;
    });
    if (!controllerTargetsMe) continue;

    if (isQuestionVisible(controller, questions, answers, visiting)) {
      return true;
    }
  }
  return false;
}

/**
 * Order questions for the *builder/editor* view: every conditional follow-up is
 * pulled directly beneath the question that reveals it (in option order, nested
 * for chained branches), regardless of its stored `order`.
 *
 * Unlike {@link getVisibleQuestionsInDisplayOrder}, this ignores answers and
 * never drops anything — it always returns the full set. It exists because, in
 * the editor, a controller is typically created *after* its target questions, so
 * a follow-up commonly has a lower `order` and would render *above* its
 * controller (appearing as an orphaned/indented row). Grouping each follow-up
 * under its trigger makes the conditional hierarchy read top-to-bottom.
 *
 * Targets are resolved by stable `branchKey` or, as a fallback, by builder `id`
 * (an option can still reference its target by id before a branchKey is minted).
 */
export function getQuestionsInBuilderDisplayOrder<T extends ConditionalQuestionLike>(
  questions: T[]
): T[] {
  const byBranchKey = new Map<string, T>();
  const byId = new Map<string, T>();
  for (const q of questions) {
    const key = parseBranchKey(q.optionsJson);
    if (key) byBranchKey.set(key, q);
    if (q.id) byId.set(q.id, q);
  }

  const resolve = (val: string | undefined): T | undefined =>
    val ? byBranchKey.get(val) ?? byId.get(val) : undefined;

  // Question ids that are revealed by some option => conditional children.
  const childIds = new Set<string>();
  for (const q of questions) {
    const targets = parseOptionTargets(q.optionsJson);
    for (const val of Object.values(targets)) {
      const t = resolve(val);
      if (t?.id) childIds.add(t.id);
    }
  }

  const result: T[] = [];
  const emitted = new Set<string>();

  const emit = (q: T): void => {
    const id = q.id || '';
    if (id && emitted.has(id)) return;
    if (id) emitted.add(id);
    result.push(q);

    const targets = parseOptionTargets(q.optionsJson);
    const optionIndexes = Object.keys(targets)
      .map(Number)
      .filter(n => Number.isFinite(n))
      .sort((a, b) => a - b);
    for (const idx of optionIndexes) {
      const target = resolve(targets[idx]);
      if (target) emit(target);
    }
  };

  // Walk base order, emitting controllers/standalone questions where they sit
  // and letting each controller pull its follow-ups in right beneath it.
  for (const q of questions) {
    if (q.id && childIds.has(q.id)) continue;
    emit(q);
  }

  // Safety net: append anything not reached (e.g. a child whose controller was
  // removed) so nothing is ever dropped.
  for (const q of questions) {
    const id = q.id || '';
    if (id && emitted.has(id)) continue;
    if (id) emitted.add(id);
    result.push(q);
  }

  return result;
}

/**
 * Return the currently-visible questions in *display* order, with each
 * conditionally-revealed follow-up placed immediately after the question (and
 * answer) that reveals it — instead of at the follow-up's stored `order`.
 *
 * Why this exists: to configure conditional logic you must create the target
 * question *before* an answer can point at it, so a follow-up commonly has a
 * lower `order` than its controlling question and would otherwise sort *above*
 * it. On a form that mixes normal + conditional questions, that puts the
 * revealed question in an unexpected place (often above the controller / out of
 * view) so the reveal reads as "nothing happened". Anchoring each follow-up
 * directly beneath its trigger makes it appear inline, on the fly, exactly
 * where the customer just answered.
 *
 * This only changes *ordering*; the set of visible questions is identical to
 * filtering each question through {@link isQuestionVisible}.
 *
 * @param questions Questions in their base display order (already sorted by `order`).
 * @param answers   Map of questionId → answer value.
 */
export function getVisibleQuestionsInDisplayOrder<T extends ConditionalQuestionLike>(
  questions: T[],
  answers: Record<string, string | undefined>
): T[] {
  const conditionalKeys = getConditionalTargetKeys(questions);

  // Index conditional targets by their branchKey so a controller can resolve
  // the follow-up(s) its selected answer reveals.
  const byBranchKey = new Map<string, T>();
  for (const q of questions) {
    const key = parseBranchKey(q.optionsJson);
    if (key && conditionalKeys.has(key)) byBranchKey.set(key, q);
  }

  const isConditionalTarget = (q: T): boolean => {
    const key = parseBranchKey(q.optionsJson);
    return !!key && conditionalKeys.has(key);
  };

  const result: T[] = [];
  const emitted = new Set<string>();

  const emit = (q: T): void => {
    const id = q.id || '';
    if (id && emitted.has(id)) return;
    if (!isQuestionVisible(q, questions, answers)) return;
    if (id) emitted.add(id);
    result.push(q);

    // Inline the follow-up(s) revealed by this question's current answer,
    // in option order, recursing so chained branches stay grouped together.
    if (!id) return;
    const targets = parseOptionTargets(q.optionsJson);
    if (Object.keys(targets).length === 0) return;
    const answer = answers[id];
    if (answer == null || answer === '') return;
    const options = parseQuestionOptions(q.optionsJson);
    const selectedValues = answer.split('||');
    options.forEach((opt, idx) => {
      if (!selectedValues.includes(opt)) return;
      const targetKey = targets[idx];
      if (!targetKey) return;
      const target = byBranchKey.get(targetKey);
      if (target) emit(target);
    });
  };

  // Walk base order, emitting non-target questions where they sit and letting
  // each controller pull its revealed follow-ups in right beneath it.
  for (const q of questions) {
    if (isConditionalTarget(q)) continue;
    emit(q);
  }

  // Safety net: never drop a visible question. If a target is visible but its
  // controller wasn't (so it wasn't reached above), append it in base order.
  for (const q of questions) {
    const id = q.id || '';
    if (id && emitted.has(id)) continue;
    if (isQuestionVisible(q, questions, answers)) {
      if (id) emitted.add(id);
      result.push(q);
    }
  }

  return result;
}
